import { useCallback, useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';
import GalleryItem from '@/pages/home/components/GalleryItem';
import { gallery } from '@/mocks/home';

export default function Gallery() {
  const [active, setActive] = useState<number | null>(null);
  const isOpen = active !== null;

  // Ref estritamente para o scroll interno do texto
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => setActive(null), []);
  const goPrev = useCallback(
    () => setActive((i) => (i === null ? i : (i - 1 + gallery.length) % gallery.length)),
    []
  );
  const goNext = useCallback(
    () => setActive((i) => (i === null ? i : (i + 1) % gallery.length)),
    []
  );

  // Faz o reset do scroll para o topo (0) sempre que o item ativo mudar
  useEffect(() => {
    if (active !== null && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [active]);

  // While the lightbox is open: lock the page scroll and wire the keyboard.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') goPrev();
      if (event.key === 'ArrowRight') goNext();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, close, goPrev, goNext]);

  const current = active !== null ? gallery[active] : null;

  return (
    <section id="galeria" className="relative w-full bg-background-50 py-20 md:py-28">
      <div className="mx-auto w-full max-w-container px-4 md:px-6 lg:px-10">
        <SectionHeading
          eyebrow="Serviços que coordenamos"
          title="Espaços comuns cuidados ao detalhe."
          description="Uma amostra das zonas comuns que gerimos. Clique em cada uma para descobrir todos os serviços de manutenção que coordenamos."
        />

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {gallery.map((item, index) => (
            <Reveal key={item.id} delay={(index % 2) * 90} className="h-full">
              <GalleryItem
                src={item.src}
                alt={item.alt}
                title={item.title}
                onOpen={() => setActive(index)}
              />
            </Reveal>
          ))}
        </div>
      </div>

      {current && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={current.title}
          onClick={close}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-secondary-950/85 p-4 backdrop-blur-sm md:p-8"
        >
          <div
            className="animate-fade-up relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-background-50 md:flex-row"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={close}
              aria-label="Fechar"
              className="absolute right-3 top-3 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-secondary-950/70 text-background-50 transition-colors duration-300 hover:bg-secondary-950"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="relative flex items-center justify-center bg-secondary-950 md:min-h-[420px] md:w-[42%] md:shrink-0">
              <img
                src={current.src}
                alt={current.alt}
                title={`${current.title} · Porta31 Condomínios`}
                className="max-h-[36vh] w-full object-contain md:h-full md:max-h-none md:object-cover"
              />
              <button
                type="button"
                onClick={goPrev}
                aria-label="Zona anterior"
                className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/85 text-foreground-900 transition-colors duration-300 hover:bg-background-50"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Próxima zona"
                className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/85 text-foreground-900 transition-colors duration-300 hover:bg-background-50"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            {/* Aqui adicionámos o ref={scrollContainerRef} ao contentor de texto */}
            <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-5 md:p-7">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-accent-900">
                  {current.category}
                </span>
              </div>
              <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                {current.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                {current.excerpt}
              </p>

              <div className="mt-5 border-t border-background-200 pt-4">
                <span className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                  Serviços que coordenamos
                </span>
                <div className="mt-4 space-y-4">
                  {current.content.map((block, index) => (
                    <div key={index}>
                      {block.heading && (
                        <h4 className="font-heading text-sm font-bold leading-snug text-foreground-950 md:text-base">
                          {block.heading}
                        </h4>
                      )}
                      <p className="mt-1 text-sm leading-relaxed text-foreground-600 md:text-base">
                        {block.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}